/**
 * @flow
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { startOfDay } from 'date-fns';
import { Loader } from 'semantic-ui-react';
import Measure from 'react-measure';
import { uniqBy, property, get, filter } from 'lodash';

import Timetable from '../../../../components/timetable/Timetable';
import AppointmentCell from './AppointmentCell';
import AppointmentCardContainer from './AppointmentCardContainer';
import AppointmentColumn from '../../containers/timetable/AppointmentColumn';
import Legend from '../../components/timetable/Legend';
import { setCellHeight } from '../../../../actions/appointments';

import type { Salon, Service } from '../../../../type';

type AppointmentTimetableProps = {
    loading: boolean,
    salon: Salon,
    date: Object,
    services: Array<Service>,
    allowToAddAppointments: boolean,
    updateAppointmentClient: Function,
};

// Insert legend into timetable to make that work
// legend={<Legend services={services} />}

const AppointmentTimetable = ({
    loading,
    salon,
    date,
    services,
    providers,
    allowToAddAppointments,
    updateAppointmentClient,
    onHeaderClick,
    renderColumn,
    renderCell,
    renderCards,
    renderHeader,
    noScheduleMessage,
    setCellHeight,
}: AppointmentTimetableProps) => {
    const updateDimensions = useCallback(() => {
        const cell = document.querySelector('.timetable .cell');
        const headerCell = document.querySelector(
            '.timetable .timeline-header',
        );
        if (cell && headerCell) {
            setCellHeight(cell.offsetHeight, headerCell.offsetHeight);
        }
    }, [setCellHeight]);

    // Track viewport resizes; reset the cached cell height on unmount.
    useEffect(() => {
        window.addEventListener('resize', updateDimensions);
        updateDimensions();
        return () => {
            window.removeEventListener('resize', updateDimensions);
            setCellHeight(null, null);
        };
    }, [updateDimensions, setCellHeight]);

    // Recompute cell height whenever the set of services changes (added,
    // removed, or count differs) — mirrors the old componentDidUpdate guard.
    const servicesCount = services ? services.length : -1;
    const didMountRef = useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        updateDimensions();
    }, [servicesCount, updateDimensions]);

    // Show spinner while loading timetable data
    if (!salon && loading) {
        return <Loader size="big" inline="centered" active />;
    }

    return (
        <Measure
            onResize={contentRect => {
                updateDimensions();
            }}
        >
            {({ measureRef }) => (
                <div style={{ height: '100%' }} ref={measureRef}>
                    <Timetable
                        loading={loading}
                        schedule={get(salon, 'settings.schedule', {})}
                        providers={providers}
                        date={startOfDay(new Date(date))}
                        renderColumn={renderColumn}
                        renderCell={renderCell}
                        renderCards={renderCards}
                        renderHeader={renderHeader}
                        services={services}
                        noScheduleMessage={noScheduleMessage}
                    />
                </div>
            )}
        </Measure>
    );
};

const mapDispatchToProps = {
    setCellHeight,
};

export default connect(
    null,
    mapDispatchToProps,
)(AppointmentTimetable);
